import React, { useEffect, useMemo } from 'react';

import { I18nextProvider } from 'react-i18next';

import { createI18n } from './i18n';
import { subscribeLocaleChange } from './localeBroadcast';

export type I18nProviderProps = {
	baseUrl: string;
	enterpriseId?: string | undefined;
	initialLocale?: string | undefined;
	// Supplies the host app's current Bearer access token for the auth-gated
	// catalog endpoint. Kept as a getter (not a value) so a token that resolves
	// after mount — e.g. once login completes — is picked up on the next load.
	getToken?: (() => string | null | undefined) | undefined;
	children: React.ReactNode;
};

export type LoadLocaleFn = (locale: string) => Promise<void>;

export const I18nLoadContext = React.createContext<LoadLocaleFn>(
	async () => {},
);

export const useI18nLoad = (): LoadLocaleFn =>
	React.useContext(I18nLoadContext);

export function I18nProvider({
	baseUrl,
	enterpriseId,
	initialLocale,
	getToken,
	children,
}: I18nProviderProps) {
	const { i18n, loadLocale } = useMemo(
		() => createI18n({ baseUrl, enterpriseId, initialLocale, getToken }),
		[baseUrl, enterpriseId, initialLocale, getToken],
	);

	// Sync to locale changes broadcast by any other shell (e.g. the host nav's
	// language switcher) so a mounted MFE re-renders in the new language live.
	useEffect(() => subscribeLocaleChange((locale) => void loadLocale(locale)), [
		loadLocale,
	]);

	return (
		<I18nextProvider i18n={i18n}>
			<I18nLoadContext.Provider value={loadLocale}>
				{children}
			</I18nLoadContext.Provider>
		</I18nextProvider>
	);
}
